import {
  FilterGroup,
  FilterToolbarProps,
} from "@components/common/data-grid/adm-data-grid-types";
import { FilterDropdown } from "@components/common/data-grid/toolbar/filter-dropdown";
import {
  getSelectedOptionIds,
  setGroupSelection,
} from "@components/common/data-grid/toolbar/toolbar-helper";
import { Button } from "@flat-ui";
import { Grid } from "@mui-wrapper";
import { GeneralTableEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

/** Wrapper to show the filter toolbar */
export function FilterToolbar({
  filterGroups,
  tableType,
  activeFilters,
  setActiveFilters,
}: FilterToolbarProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();

  const hasActiveFilters = !!activeFilters?.items?.length;

  /** Applies the new selection of a group and tracks any option that was just added */
  function handleGroupChange(
    group: FilterGroup,
    nextSelectedIds: string[]
  ): void {
    const previousIds = getSelectedOptionIds(group, activeFilters);
    const addedId = nextSelectedIds.find((id) => !previousIds.includes(id));

    if (addedId) {
      trackEvent({
        name: GeneralTableEvents.applyFilter,
        props: {
          location: tableType,
          filterType: addedId,
        },
      });

      group.options.find((option) => option.id === addedId)?.onSelect?.();
    }

    setActiveFilters((prev) => setGroupSelection(group, nextSelectedIds, prev));
  }

  /** Removes every active filter across all groups */
  function clearAllFilters(): void {
    trackEvent({
      name: GeneralTableEvents.clearFilters,
      props: {
        location: tableType,
      },
    });

    setActiveFilters((prev) => ({
      ...prev,
      items: [],
    }));
  }

  return (
    <Grid container spacing={1} sx={{ alignItems: "center" }}>
      {filterGroups.map((group) => (
        <FilterDropdown
          key={group.id}
          label={group.label}
          options={group.options}
          selectedIds={getSelectedOptionIds(group, activeFilters)}
          onChange={(nextSelectedIds) =>
            handleGroupChange(group, nextSelectedIds)
          }
        />
      ))}

      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearAllFilters}>
          Clear All Filters
        </Button>
      )}
    </Grid>
  );
}
